import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { usePrivy } from '@privy-io/react-auth';
import { motion } from 'framer-motion';
import { Swords, Clock, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { GameIcon } from '@/components/ui/GameIcon';
import { getGameName } from '@/lib/gameConfig';
import { api } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';

export function ChallengePage() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { authenticated, login } = usePrivy();

  const { data: wager, isLoading, error } = useQuery({
    queryKey: ['challenge', code],
    queryFn: () => api.getWagerByCode(code!),
    enabled: !!code,
  });

  const acceptMutation = useMutation({
    mutationFn: () => api.acceptWager(wager!.id),
    onSuccess: (updatedWager) => {
      navigate(`/wagers/${updatedWager.id}`);
    },
  });

  const handleAccept = () => {
    if (!authenticated) {
      login();
      return;
    }
    acceptMutation.mutate();
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (error || !wager) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4 text-center">
        <Swords className="mb-4 h-16 w-16 text-muted-foreground" />
        <h1 className="font-heading text-2xl font-bold">Challenge Not Found</h1>
        <p className="mt-2 text-muted-foreground">
          This challenge may have expired or been cancelled.
        </p>
        <Button variant="outline" className="mt-6" onClick={() => navigate('/')}>
          Go Home
        </Button>
      </div>
    );
  }

  if (wager.status !== 'open') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4 text-center">
        <Swords className="mb-4 h-16 w-16 text-muted-foreground" />
        <h1 className="font-heading text-2xl font-bold">Challenge Unavailable</h1>
        <p className="mt-2 text-muted-foreground">
          This challenge has already been accepted or is no longer available.
        </p>
        <Button variant="outline" className="mt-6" onClick={() => navigate('/wagers')}>
          Browse Challenges
        </Button>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md"
      >
        <Card className="overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-violet-600 to-blue-600 p-6 text-center text-white">
            <Swords className="mx-auto mb-4 h-12 w-12" />
            <h1 className="font-heading text-2xl font-bold">You've Been Challenged!</h1>
            <p className="mt-1 opacity-90">Accept to compete for stakes</p>
          </div>

          <CardContent className="p-6">
            {/* Challenger */}
            <div className="mb-6 flex items-center gap-4">
              <Avatar className="h-14 w-14">
                <AvatarImage src={wager.creator.avatarUrl} />
                <AvatarFallback className="bg-primary text-primary-foreground">
                  {wager.creator.displayName[0]}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="font-heading text-lg font-bold">{wager.creator.displayName}</p>
                <p className="text-sm text-muted-foreground">Challenger</p>
              </div>
            </div>

            {/* Details */}
            <div className="space-y-3 rounded-xl bg-muted/50 p-4">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Game</span>
                <div className="flex items-center gap-2">
                  <GameIcon gameCode={wager.gameCode} size="sm" />
                  <span className="font-medium">{getGameName(wager.gameCode)}</span>
                </div>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Type</span>
                <span className="font-medium">{wager.wagerType}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Stake</span>
                <span className="font-heading text-xl font-bold text-primary">
                  {formatCurrency(wager.amount)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total Pot</span>
                <span className="font-medium">
                  {formatCurrency(parseFloat(wager.amount) * 2)}
                </span>
              </div>
            </div>

            {/* Expiry */}
            {wager.expiresAt && (
              <div className="mt-4 flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Clock className="h-4 w-4" />
                <span>Expires in 24 hours</span>
              </div>
            )}

            {/* Actions */}
            <div className="mt-6 space-y-3">
              <Button
                variant="gradient"
                size="lg"
                className="w-full gap-2"
                onClick={handleAccept}
                disabled={acceptMutation.isPending}
              >
                {acceptMutation.isPending ? (
                  'Accepting...'
                ) : authenticated ? (
                  <>
                    Accept Challenge <ArrowRight className="h-4 w-4" />
                  </>
                ) : (
                  <>
                    Sign In to Accept <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </Button>
              <Button
                variant="ghost"
                size="lg"
                className="w-full"
                onClick={() => navigate('/wagers')}
              >
                Decline
              </Button>
            </div>

            {/* Info */}
            <p className="mt-6 text-center text-xs text-muted-foreground">
              By accepting, you agree to fund {formatCurrency(wager.amount)} + 1% platform fee.
              Winner takes all.
            </p>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
